import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { Link as ScrollLink } from 'react-scroll';
import { Link } from 'react-router-dom';
import { FaArrowRight, FaBars, FaTimes } from 'react-icons/fa';
import SignedImage from '../common/SignedImage';

const Header = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  padding: 0.85rem 1rem 0;
  pointer-events: none;
`;

const Shell = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  pointer-events: auto;
`;

const NavCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: ${({ isScrolled }) => (isScrolled ? '0.78rem 1rem' : '0.95rem 1.1rem')};
  border-radius: 24px;
  background: ${({ isScrolled }) =>
    isScrolled
      ? 'rgba(10, 18, 32, 0.86)'
      : 'linear-gradient(180deg, rgba(255, 255, 255, 0.88), rgba(255, 255, 255, 0.76))'};
  border: 1px solid
    ${({ isScrolled }) => (isScrolled ? 'rgba(148, 163, 184, 0.18)' : 'rgba(148, 163, 184, 0.22)')};
  box-shadow: ${({ isScrolled }) =>
    isScrolled
      ? '0 18px 50px rgba(15, 23, 42, 0.28)'
      : '0 18px 50px rgba(148, 163, 184, 0.18)'};
  backdrop-filter: blur(18px);
  transition: padding 0.25s ease, background 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
`;

const Brand = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.85rem;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
`;

const LogoWrap = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 15px;
  display: grid;
  place-items: center;
  background: ${({ isScrolled }) =>
    isScrolled
      ? 'linear-gradient(145deg, rgba(99, 102, 241, 0.22), rgba(14, 165, 233, 0.18))'
      : 'linear-gradient(145deg, #ffffff, #eef4ff)'};
  border: 1px solid ${({ isScrolled }) => (isScrolled ? 'rgba(255,255,255,0.08)' : 'rgba(148, 163, 184, 0.18)')};
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.5);
`;

const Logo = styled(SignedImage)`
  width: 34px;
  height: 34px;
  object-fit: contain;
`;

const BrandText = styled.div`
  display: grid;
  gap: 0.1rem;
  text-align: left;
`;

const BrandEyebrow = styled.span`
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ isScrolled }) => (isScrolled ? 'rgba(226, 232, 240, 0.7)' : '#4f46e5')};
`;

const BrandTitle = styled.span`
  font-size: 1.05rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: ${({ isScrolled }) => (isScrolled ? '#ffffff' : '#0f172a')};
`;

const DesktopNav = styled.div`
  display: none;

  @media (min-width: 980px) {
    display: flex;
    align-items: center;
    gap: 0.9rem;
  }
`;

const NavCluster = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.34rem;
  border-radius: 999px;
  background: ${({ isScrolled }) =>
    isScrolled ? 'rgba(255, 255, 255, 0.06)' : 'rgba(15, 23, 42, 0.04)'};
  border: 1px solid ${({ isScrolled }) => (isScrolled ? 'rgba(255,255,255,0.08)' : 'rgba(148,163,184,0.14)')};
`;

const sharedLinkStyles = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  padding: 0.72rem 1rem;
  border-radius: 999px;
  text-decoration: none;
  font-size: 0.92rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: ${({ isScrolled }) => (isScrolled ? 'rgba(241, 245, 249, 0.88)' : '#334155')};
  transition: background 0.2s ease, color 0.2s ease, transform 0.2s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-1px);
    background: ${({ isScrolled }) =>
      isScrolled ? 'rgba(255, 255, 255, 0.08)' : 'rgba(79, 70, 229, 0.08)'};
    color: ${({ isScrolled }) => (isScrolled ? '#ffffff' : '#1e293b')};
  }
`;

const NavScrollLink = styled(ScrollLink)`
  ${sharedLinkStyles}
`;

const NavRouteLink = styled(Link)`
  ${sharedLinkStyles}
`;

const Actions = styled.div`
  display: none;

  @media (min-width: 980px) {
    display: flex;
    align-items: center;
    gap: 0.7rem;
  }
`;

const GhostAction = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 42px;
  padding: 0.8rem 1rem;
  border-radius: 999px;
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 700;
  color: ${({ isScrolled }) => (isScrolled ? '#ffffff' : '#0f172a')};
  background: ${({ isScrolled }) => (isScrolled ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.7)')};
  border: 1px solid ${({ isScrolled }) => (isScrolled ? 'rgba(255,255,255,0.1)' : 'rgba(148,163,184,0.2)')};
  transition: transform 0.2s ease, background 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    background: ${({ isScrolled }) => (isScrolled ? 'rgba(255,255,255,0.1)' : '#ffffff')};
  }
`;

const PrimaryAction = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  min-height: 44px;
  padding: 0.84rem 1.15rem;
  border-radius: 999px;
  text-decoration: none;
  font-size: 0.92rem;
  font-weight: 800;
  color: #ffffff;
  background: linear-gradient(90deg, #4f46e5 0%, #0ea5e9 100%);
  box-shadow: 0 12px 30px rgba(79, 70, 229, 0.24);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 16px 34px rgba(79, 70, 229, 0.28);
  }
`;

const MobileToggle = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 14px;
  border: 1px solid ${({ isScrolled }) => (isScrolled ? 'rgba(255,255,255,0.12)' : 'rgba(148,163,184,0.2)')};
  background: ${({ isScrolled }) => (isScrolled ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.72)')};
  color: ${({ isScrolled }) => (isScrolled ? '#ffffff' : '#0f172a')};
  cursor: pointer;

  @media (min-width: 980px) {
    display: none;
  }
`;

const MobilePanel = styled.div`
  margin-top: 0.75rem;
  padding: 0.8rem;
  border-radius: 24px;
  background: linear-gradient(180deg, rgba(10, 18, 32, 0.96), rgba(17, 24, 39, 0.94));
  border: 1px solid rgba(148, 163, 184, 0.16);
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.28);
  backdrop-filter: blur(18px);
  display: ${({ isOpen }) => (isOpen ? 'grid' : 'none')};
  gap: 0.4rem;

  @media (min-width: 980px) {
    display: none;
  }
`;

const MobileLink = styled(ScrollLink)`
  display: inline-flex;
  align-items: center;
  min-height: 46px;
  padding: 0.85rem 0.9rem;
  border-radius: 16px;
  color: #e2e8f0;
  text-decoration: none;
  font-size: 0.98rem;
  font-weight: 700;
  background: rgba(255, 255, 255, 0.04);
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
  }
`;

const MobileRouteLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  min-height: 46px;
  padding: 0.85rem 0.9rem;
  border-radius: 16px;
  color: #e2e8f0;
  text-decoration: none;
  font-size: 0.98rem;
  font-weight: 700;
  background: rgba(255, 255, 255, 0.04);

  &:hover {
    background: rgba(255, 255, 255, 0.08);
  }
`;

const MobilePrimary = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  min-height: 48px;
  margin-top: 0.35rem;
  padding: 0.9rem 1rem;
  border-radius: 16px;
  text-decoration: none;
  font-size: 0.96rem;
  font-weight: 800;
  color: #ffffff;
  background: linear-gradient(90deg, #4f46e5 0%, #0ea5e9 100%);
`;

const navItems = [
  { kind: 'scroll', label: 'Home', to: 'hero' },
  { kind: 'scroll', label: 'Showcase', to: 'app-showcase' },
  { kind: 'scroll', label: 'Features', to: 'features-section' },
  { kind: 'scroll', label: 'FAQ', to: 'seo-faq' },
];

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 24);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const closeMenuOnResize = () => {
      if (window.innerWidth >= 980) {
        setIsOpen(false);
      }
    };
    window.addEventListener('resize', closeMenuOnResize);
    return () => window.removeEventListener('resize', closeMenuOnResize);
  }, []);

  return (
    <Header>
      <Shell>
        <NavCard isScrolled={isScrolled}>
          <Brand onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <LogoWrap isScrolled={isScrolled}>
              <Logo
                src="https://skhool-media-prod-193482297265.s3.ap-south-1.amazonaws.com/skhool/prod/product/public/images/skhool_logo.png"
                alt="Skhool Logo"
              />
            </LogoWrap>
            <BrandText>
              <BrandEyebrow isScrolled={isScrolled}>AI Digital School</BrandEyebrow>
              <BrandTitle isScrolled={isScrolled}>Skhool</BrandTitle>
            </BrandText>
          </Brand>

          <DesktopNav>
            <NavCluster isScrolled={isScrolled}>
              {navItems.map((item) =>
                item.kind === 'scroll' ? (
                  <NavScrollLink
                    key={item.label}
                    isScrolled={isScrolled}
                    to={item.to}
                    spy
                    smooth
                    offset={-96}
                    duration={500}
                  >
                    {item.label}
                  </NavScrollLink>
                ) : (
                  <NavRouteLink key={item.label} isScrolled={isScrolled} to={item.to}>
                    {item.label}
                  </NavRouteLink>
                )
              )}
            </NavCluster>

            <Actions>
              <GhostAction isScrolled={isScrolled} href="https://webdemo.skhool.in/">
                Website Demo
              </GhostAction>
              <PrimaryAction to="/login">
                Admin Login
                <FaArrowRight />
              </PrimaryAction>
            </Actions>
          </DesktopNav>

          <MobileToggle
            type="button"
            isScrolled={isScrolled}
            aria-label={isOpen ? 'Close navigation menu' : 'Open navigation menu'}
            onClick={() => setIsOpen((prev) => !prev)}
          >
            {isOpen ? <FaTimes /> : <FaBars />}
          </MobileToggle>
        </NavCard>

        <MobilePanel isOpen={isOpen}>
          {navItems.map((item) =>
            item.kind === 'scroll' ? (
              <MobileLink
                key={item.label}
                to={item.to}
                spy
                smooth
                offset={-96}
                duration={500}
                onClick={() => setIsOpen(false)}
              >
                {item.label}
              </MobileLink>
            ) : (
              <MobileRouteLink key={item.label} to={item.to} onClick={() => setIsOpen(false)}>
                {item.label}
              </MobileRouteLink>
            )
          )}
          <MobileRouteLink to="/pricing" onClick={() => setIsOpen(false)}>
            Pricing
          </MobileRouteLink>
          <MobileRouteLink as="a" href="https://webdemo.skhool.in/" onClick={() => setIsOpen(false)}>
            Website Demo
          </MobileRouteLink>
          <MobilePrimary to="/login" onClick={() => setIsOpen(false)}>
            Admin Login
            <FaArrowRight />
          </MobilePrimary>
        </MobilePanel>
      </Shell>
    </Header>
  );
};

export default Navbar;
